/**
 * Admin Customer Requests — filtered list view.
 *
 * Reached from the "Open customer follow-ups" KPI tile on `/admin/dashboard`.
 * URL query params drive the filters so links from the tile can deep-link
 * directly into proposal-only / assessment-only views.
 *
 *   /admin/customer-requests             — open only, both types (default)
 *   /admin/customer-requests?type=proposal
 *   /admin/customer-requests?type=assessment
 *   /admin/customer-requests?status=all  — show resolved too
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Send, CalendarCheck2, ExternalLink, Loader2, MailCheck, ChevronRight, FileText } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import api, { formatApiError } from "@/lib/api";
import { TankCleanContext } from "@/components/TankCleanContext";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "new",                     label: "New",                     tone: "bg-cyan-100 text-cyan-800",    open: true },
  { value: "contacted",               label: "Contacted",               tone: "bg-amber-100 text-amber-800",  open: true },
  { value: "in_progress",             label: "In progress",             tone: "bg-amber-100 text-amber-800",  open: true },
  { value: "proposal_being_prepared", label: "Estimate being prepared", tone: "bg-amber-100 text-amber-800",  open: true },
  { value: "assessment_requested",    label: "Assessment requested",    tone: "bg-amber-100 text-amber-800",  open: true },
  { value: "proposal_sent",           label: "Estimate sent",           tone: "bg-indigo-100 text-indigo-800", open: false },
  { value: "assessment_booked",       label: "Assessment booked",       tone: "bg-violet-100 text-violet-800", open: false },
  { value: "closed",                  label: "Closed",                  tone: "bg-slate-200 text-slate-700",  open: false },
  { value: "cancelled",               label: "Cancelled",               tone: "bg-slate-200 text-slate-700",  open: false },
  { value: "duplicate",               label: "Duplicate",               tone: "bg-slate-200 text-slate-700",  open: false },
];

const TYPE_LABEL = {
  proposal:   { label: "Instant Estimate",                  icon: Send },
  assessment: { label: "Free Post Water Test Assessment",   icon: CalendarCheck2 },
};

const PRIORITY_TONE = {
  critical: "bg-rose-100 text-rose-800",
  high:     "bg-rose-50 text-rose-700",
  medium:   "bg-amber-50 text-amber-700",
  low:      "bg-slate-100 text-slate-700",
  info:     "bg-slate-100 text-slate-700",
};


export default function AdminCustomerRequests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter   = searchParams.get("type")   || "all";   // all | proposal | assessment
  const statusFilter = searchParams.get("status") || "open";  // open | all | <specific>

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [estimateBusyId, setEstimateBusyId] = useState(null);
  const navigate = useNavigate();

  // Jun 2026 — one-click pre-filled estimate from an Instant Estimate
  // request. Reuses/creates the auto-matched draft, then jumps to it.
  const openEstimate = async (requestId) => {
    setEstimateBusyId(requestId);
    try {
      const { data } = await api.post(`/admin/customer-requests/${requestId}/estimate`);
      if (data.created) {
        const skipped = (data.skipped || []).length;
        toast.success(`Draft estimate created with auto-matched services${skipped ? ` — ${skipped} need a product/price` : ""}`);
      }
      navigate(`/admin/proposals?focus=${data.proposal_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open estimate");
    } finally {
      setEstimateBusyId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.type_filter = typeFilter;
      if (statusFilter === "open") params.open_only = true;
      else if (statusFilter !== "all") params.status_filter = statusFilter;
      const { data } = await api.get("/admin/customer-requests", { params });
      setRequests(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [typeFilter, statusFilter]);

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const { data } = await api.patch(`/admin/customer-requests/${id}`, { status: newStatus });
      // If we're filtering by `open` and the new status is closed → drop the row
      const newOpt = STATUS_OPTIONS.find((s) => s.value === newStatus);
      if (statusFilter === "open" && newOpt && !newOpt.open) {
        setRequests((prev) => prev.filter((r) => r.id !== id));
      } else {
        setRequests((prev) => prev.map((r) => (r.id === id ? data : r)));
      }
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all" || (key === "status" && value === "open")) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const counts = {
    proposal:   requests.filter((r) => r.type === "proposal").length,
    assessment: requests.filter((r) => r.type === "assessment").length,
  };

  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Admin</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2">Customer follow-up requests</h1>
            <p className="text-sm text-slate-500 mt-2">Customer-initiated requests off published Summary Reports.</p>
          </div>
          <Link to="/admin/dashboard" className="text-xs text-slate-500 hover:text-cyan-700 inline-flex items-center gap-1">← Back to dashboard</Link>
        </div>

        <div data-testid="customer-requests-filter-bar" className="mb-6 flex flex-wrap items-center gap-2">
          <FilterChip label="All types"   active={typeFilter === "all"}        onClick={() => setFilter("type", "all")} testId="filter-type-all" />
          <FilterChip label={`Estimate requests${typeFilter==='proposal' ? ` · ${counts.proposal}` : ''}`}      active={typeFilter === "proposal"}   onClick={() => setFilter("type", "proposal")} icon={Send} testId="filter-type-proposal" />
          <FilterChip label={`Assessment requests${typeFilter==='assessment' ? ` · ${counts.assessment}` : ''}`} active={typeFilter === "assessment"} onClick={() => setFilter("type", "assessment")} icon={CalendarCheck2} testId="filter-type-assessment" />
          <div className="w-px h-6 bg-slate-200 mx-1" />
          <FilterChip label="Open only" active={statusFilter === "open"} onClick={() => setFilter("status", "open")} testId="filter-status-open" />
          <FilterChip label="Show all"  active={statusFilter === "all"}  onClick={() => setFilter("status", "all")}  testId="filter-status-all" />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : requests.length === 0 ? (
          <div data-testid="customer-requests-empty" className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center">
            <MailCheck className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            <div className="text-sm font-semibold text-slate-700">No requests match the current filters.</div>
            <div className="text-xs text-slate-500 mt-1">Adjust the filters above or wait for new customer follow-ups.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => <RequestCard key={r.id} request={r} onStatus={updateStatus} updating={updatingId === r.id} onOpenEstimate={openEstimate} estimateBusy={estimateBusyId === r.id} />)}
          </div>
        )}
      </div>
    </PortalShell>
  );
}


function FilterChip({ label, active, onClick, icon: Icon, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? "bg-slate-900 border-slate-900 text-white"
          : "bg-white border-slate-300 text-slate-700 hover:border-cyan-500 hover:text-cyan-700"
      }`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}


function RequestCard({ request, onStatus, updating, onOpenEstimate, estimateBusy }) {
  const meta = TYPE_LABEL[request.type] || { label: request.type, icon: MailCheck };
  const Icon = meta.icon;
  const statusOpt = STATUS_OPTIONS.find((s) => s.value === request.status) || STATUS_OPTIONS[0];
  const triggeredCats = uniqueCategories(request.recommendations);
  const topPriority = highestPriority(request.recommendations);

  return (
    <div data-testid={`customer-request-row-${request.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-cyan-600" />
          <div>
            <div className="font-display font-semibold text-slate-900 text-sm">{meta.label}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {formatDateTimeAU(request.created_at)}
            </div>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusOpt.tone}`}>{statusOpt.label}</span>
      </div>

      <div className="grid sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Customer</div>
          <div data-testid={`customer-request-name-${request.id}`} className="text-slate-900 font-semibold">{request.customer?.name || "—"}</div>
          {request.customer?.email && <a data-testid={`customer-request-email-${request.id}`} href={`mailto:${request.customer.email}`} className="text-cyan-700 block">{request.customer.email}</a>}
          {request.customer?.phone && <a data-testid={`customer-request-phone-${request.id}`} href={`tel:${request.customer.phone}`} className="text-slate-700 block">{request.customer.phone}</a>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Test reference</div>
          <div className="text-slate-900 font-mono">{request.test_reference || request.test_id?.slice(0, 8) || "—"}</div>
          <div className="text-slate-600">{request.test_name || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">Health band</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-900 font-semibold capitalize">{request.health_band || "—"}</span>
            {topPriority && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${PRIORITY_TONE[topPriority]}`}>{topPriority}</span>}
          </div>
          {request.health_score != null && <div className="text-slate-600 mt-0.5">Score {request.health_score}/100</div>}
        </div>
      </div>

      {(triggeredCats.length > 0 || request.suggested_services?.length > 0) && (
        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-2.5 text-[11px] space-y-1">
          {triggeredCats.length > 0 && (
            <div><span className="text-slate-500 font-semibold uppercase tracking-wider mr-1.5">Triggered:</span>{triggeredCats.map((c) => <span key={c} className="inline-block mr-1.5 px-2 py-0.5 bg-white rounded-full border border-slate-200 text-slate-800">{prettyCat(c)}</span>)}</div>
          )}
          {request.suggested_services?.length > 0 && (
            <div><span className="text-slate-500 font-semibold uppercase tracking-wider mr-1.5">Services:</span><span className="text-slate-800">{request.suggested_services.map(prettySvc).join(" · ")}</span></div>
          )}
          {request.tank_clean_recommended && (
            <TankCleanContext recommended={request.tank_clean_recommended} context={request.tank_clean_context} testIdPrefix={`request-tank-context-${request.id}`} />
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</div>
          <select
            data-testid={`customer-request-status-${request.id}`}
            value={request.status}
            onChange={(e) => onStatus(request.id, e.target.value)}
            disabled={updating}
            className="text-xs rounded-lg border border-slate-300 px-2 py-1.5 focus:outline-none focus:border-cyan-500"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {updating && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
        {request.type === "proposal" && request.test_id && (
          <button
            type="button"
            data-testid={`customer-request-open-estimate-${request.id}`}
            disabled={estimateBusy}
            onClick={() => onOpenEstimate(request.id)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-slate-900 hover:bg-cyan-600 disabled:opacity-50 transition-colors"
          >
            {estimateBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            Open pre-filled estimate <ChevronRight className="w-3 h-3" />
          </button>
        )}
        {request.test_id && (
          <Link
            data-testid={`customer-request-open-lab-${request.id}`}
            to={`/admin/lab/${request.test_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-cyan-700 hover:bg-cyan-50 border border-cyan-200"
          >
            Open lab report <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </div>
  );
}


function uniqueCategories(recs) {
  if (!Array.isArray(recs)) return [];
  return [...new Set(recs.map((r) => r?.category).filter(Boolean))];
}

function highestPriority(recs) {
  if (!Array.isArray(recs)) return null;
  const order = ["critical", "high", "medium", "low", "info"];
  for (const p of order) if (recs.some((r) => r?.priority === p)) return p;
  return null;
}

function prettyCat(c) {
  return c.replace(/_/g, " ");
}
function prettySvc(s) {
  return s.replace(/_/g, " ");
}
